import React from 'react';
import { Gap } from 'lib/components/styles/layout';
import { useFormikContext } from 'formik';
import { ILandingPageBuilderForm } from '../../types';
import {
  getLandingPageBuilderLogos,
  LP_BUILDER_FORM_MODE,
  LpBuilderFormMode,
} from '../../constants';
import { FormSection } from './shared/FormSection';
import { FormContainer } from './shared/FormContainer';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import { FormikSwitchField } from 'lib/components/formik/FormikSwitchField';
import { useCovideoTheme } from 'react-covideo-common';
import { FormikColorPicker } from 'lib/components/formik/FormikColorPicker';
import { ILandingPageBuilderBulkEditFormData } from '../../helpers/getInitialLandingPageValuesForBulkEdit';
import { useCDK } from 'lib/hooks/useCdk';
import { useAuth } from 'lib/context';

interface IFooterFormProps {
  mode: LpBuilderFormMode;
}

export const FooterForm = ({ mode }: IFooterFormProps) => {
  const { isCdkUser } = useCDK();
  const { userData } = useAuth();
  const { values, setFieldValue } = useFormikContext<
    ILandingPageBuilderForm | ILandingPageBuilderBulkEditFormData
  >();
  const themes = useCovideoTheme();
  const isBulkEditMode = mode === LP_BUILDER_FORM_MODE.BULK_EDIT;
  const landingPageBuilderLogos = getLandingPageBuilderLogos(
    userData.superAdminRole,
    isCdkUser
  );

  const handleColorPickerInputChange = (
    field: keyof ILandingPageBuilderBulkEditFormData,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const hex = e.target.value;
    if (isBulkEditMode) {
      // in bulk edit mode, allow empty value
      if (hex === '') {
        setFieldValue(field, null);
        return;
      }
    }
  };

  // landing page builder logo options should have "None" option in bulk edit mode
  const landingPageBuilderLogosOptions = isBulkEditMode
    ? [{ label: 'None', value: null }, ...landingPageBuilderLogos]
    : landingPageBuilderLogos;

  return (
    <FormContainer title='Footer'>
      <FormSection title='Color'>
        <Gap gap='8px'>
          <FormikColorPicker
            label='Footer Background Color'
            name='footerBackgroundColor'
            extendStyles={{
              labelStyles: {
                fontSize: '12px',
                color: themes.colors.neutral[80],
                fontWeight: 600,
                marginBottom: '8px',
              },
            }}
            onInputChange={e =>
              handleColorPickerInputChange('footerBackgroundColor', e)
            }
          />
        </Gap>
      </FormSection>
      <FormSection title='Shadow'>
        <FormikSwitchField
          name='showFooterShadow'
          label='Show Shadow'
          extendStyles={{
            labelStyles: {
              fontSize: '15px',
              lineHeight: '24px',
              fontWeight: 500,
              color: themes.colors.neutral[100],
            },
          }}
          onChange={() => {
            setFieldValue('showFooterShadow', values.showFooterShadow ? 0 : 1);
          }}
        />
      </FormSection>
      <FormSection title='Logo'>
        <FormikDropdownField
          name={`footerLogo`}
          options={landingPageBuilderLogosOptions}
        />
      </FormSection>
    </FormContainer>
  );
};
